import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragEndEvent } from 'leaflet';
import 'leaflet.markercluster';
import { Box, Spinner } from '@chakra-ui/react';
import L from 'leaflet';
import moment from 'moment';

import { useApi } from '@app/hooks';
import { Activity } from '@app/api/socket-service';
import { USER_DATE_FORMAT } from '@app/utils';

import { activitiesCluster } from './map-items/activities-сluster';
import { activityPoint } from './map-items/activity-point';
import { getActivityTitle } from './utils/get-activity-title';
import { ActivityDrawer } from './components/drawer';

export const ActivitiesMap = () => {
  const api = useApi();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [filters, setFilters] = useState<{}>({}); 
  const [isLoading, setIsLoading] = useState(false);

  const mapRef = useRef<HTMLDivElement | null>(null);
  const clusterActivitiesLayerRef = useRef<L.MarkerClusterGroup | null>(null);
  const outletLayerRef = useRef<L.LayerGroup | null>(null);
  const markers = useRef<Map<number, L.Marker>>(new Map());
  const [map, setMap] = useState<L.Map | null>(null);
  const breakFillTrigger = useRef<number>();

  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);

  const fetchActivities = useCallback(async (bbox: number[]) => {
    try {
      const response = await api.socket.getActivitiesLocation(bbox, filters);
      breakFillTrigger.current = Date.now();
      if (response.code === 200) {
        setActivities(response.data);
        if (response.data.length < 500) {
          setIsLoading(false);
        }
      }
    } catch (e) {
      setIsLoading(false);
      if (e.name === '"CanceledError"') {
        console.log('canceled');
      }
    }
  }, [api.socket, filters, map]);


  const onMapDrag = useCallback((e: DragEndEvent) => {
    const zoom = e.target.boxZoom._map.getZoom(); 
    setIsLoading(zoom < 12);
    const bounds = e.target.boxZoom._map.getBounds();
    fetchActivities([
      bounds.getNorthWest().lat,
      bounds.getNorthWest().lng,
      bounds.getSouthEast().lat,
      bounds.getSouthEast().lng,
    ]);
  }, [fetchActivities]);

  useEffect(() => {
    if (mapRef.current && !map) {
      const _map = L.map(mapRef.current, {
        zoomControl: false,
      }).setView([55.8, 37.8], 13);
      setMap(_map);
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(
        _map
      );
      outletLayerRef.current = L.layerGroup();
      outletLayerRef.current.addTo(_map);

      clusterActivitiesLayerRef.current = L.markerClusterGroup({
        // @ts-ignore
        iconCreateFunction: activitiesCluster,
        showCoverageOnHover: true,
        animateAddingMarkers: true,
        animate: true,
        chunkProgress(processedMarkers, totalMarkers, elapsedTime) {
            console.log('chunkProgress', processedMarkers, totalMarkers, elapsedTime);
        },
      });
      clusterActivitiesLayerRef.current.addTo(_map);


      const bounds = _map.getBounds();
      fetchActivities([
        bounds.getNorthWest().lat,
        bounds.getNorthWest().lng,
        bounds.getSouthEast().lat,
        bounds.getSouthEast().lng,
      ]);

      _map.on('zoomend', onMapDrag); 
      _map.on('dragend', onMapDrag);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMarkerClick = useCallback((activity: Activity, marker: L.Marker) => () => {
    markers.current?.forEach((marker) => marker.setIcon(activityPoint('point')));
    marker.setIcon(activityPoint('selected'));
    setSelectedActivity(activity);
  }, []);

  useEffect(() => {
    if (map) {
      const markerKeysToRemove = [...markers.current.keys()].filter((key) => !activities.find(({ id }) => id === key));
      markerKeysToRemove.forEach((key) => {
        clusterActivitiesLayerRef.current?.removeLayer(markers.current.get(key));
        markers.current.delete(key);
      });
      
      const activitiesToAdd = activities.filter(({ id, location }) => !markers.current.has(id) && location[0] && location[1] ); 
      (async () => {
        const currentBreakFillTrigger = breakFillTrigger.current;
        await Promise.all(activitiesToAdd.map(async (point) => new Promise<void>((resolve) => {
          setTimeout(() => {
            if (currentBreakFillTrigger !== breakFillTrigger.current) {
              resolve();
              return;
            }
            if (
              !point.location?.[0] ||
              !point.location?.[1]
            ) {
              return;
            }

            const marker = L.marker(
              point.location as [number, number],
              {
                icon: activityPoint(selectedActivity?.id !== point.id ? 'point' : 'selected'),
              }
            ).addTo(clusterActivitiesLayerRef.current);

            marker.on('click', onMarkerClick(point, marker));

            marker.bindTooltip(new L.Tooltip({ 
              direction: 'top',
              offset: [0, -26],
              content: `<b>${getActivityTitle(point.activity)}</b><br/>${moment(point.timestamp / 1000).format(USER_DATE_FORMAT)}<br/>SN: <b>${point.entityId}</b><br/>${point.username.split('-')[0]}`,
            }, ));

            markers.current.set(point.id, marker);
            resolve();
          }, 0);
        })));
        setIsLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, activities]);

  const onDrawerClose = useCallback(() => { 
    markers.current?.forEach((marker) => marker.setIcon(activityPoint('point')));
    outletLayerRef.current?.clearLayers();
    setSelectedActivity(null);
  }, []);

  const onShowOutletOnMap = useCallback((location: [number, number]) => {
    map?.flyTo(location, 15);
    outletLayerRef.current?.clearLayers();
    outletLayerRef.current?.addLayer(L.marker(location, {
      icon: L.icon({
        iconUrl: '/images/markers/interest.png',
        iconSize: [20, 36],
        shadowSize: [30, 15],
        iconAnchor: [10, 36],
        shadowAnchor: [0, 15],
        popupAnchor: [8, 30],
      }),
    }).addTo(outletLayerRef.current));

  }, [map]);

  return <>
    {/* {isLoading && <Box position='absolute' zIndex={1020} style={{ backgroundColor: 'rgba(255,255,255,0.6)' }} top={0} left={0} width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
      <Spinner />
    </Box>} */}
    <Box
      ref={mapRef}
      zIndex={1001}
      position='absolute'
      style={{ border: 'none', width: '100%', height: '100%' }}
      __css={{
        '& .activity-cluster': {
          backgroundColor: '#18d300',
          height: '30px',
          width: '30px',
          textAlign: 'center',
          border: '1px solid white',
          borderRadius: '50%',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '1px 1px 3px gray',
        },
        '& .activity-point': {
          backgroundColor: '#18d300',
          height: '36px',
          width: '36px',
          border: '1px solid white',
          borderTopLeftRadius: '50%',
          borderBottomLeftRadius: '0', 
          borderTopRightRadius: '50%',
          borderBottomRightRadius: '50%',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '1px 1px 3px gray',
          transform: 'rotate(-45deg)',

        },

        '& .leaflet-div-icon': {
          backgroundColor: 'transparent',
          border: 'none',
        }
      }}
    />
    <ActivityDrawer
      selectedActivity={selectedActivity}
      onClose={onDrawerClose}
      onShowOutletOnMap={onShowOutletOnMap}
    />
  </>;
};